import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Flame, TrendingUp, Wallet, Clock, Gift, Trophy, 
  ChevronRight, Star, ArrowUpRight, Zap, Calendar,
  Target, Award, DollarSign, Users, Eye, CreditCard,
  Download, Crown, BarChart3, Sparkles, Bell, CheckCircle,
  AlertCircle, ArrowRight, Play, Heart, MessageCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function CreatorDashboardNew() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [convertDialog, setConvertDialog] = useState(false);
  const [convertAmount, setConvertAmount] = useState('');
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [walletRes, tiersRes, earningsRes] = await Promise.all([
        api.get('/wallet'),
        api.get('/wallet/tiers'),
        api.get('/wallet/earnings?period=month')
      ]);
      
      setWallet(walletRes.data.wallet);
      setStats(walletRes.data.stats);
      setTransactions(walletRes.data.recent_transactions || []);
      setTiers(tiersRes.data.tiers || []);
      setEarnings(earningsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConvert = async () => {
    const amount = parseFloat(convertAmount);
    if (!amount || amount < (stats?.tier_benefits?.min_withdrawal || 10000)) {
      toast.error(`Minimum ${stats?.tier_benefits?.min_withdrawal || 10000} BARKA requis`);
      return;
    }
    if (amount > (wallet?.available_barka || 0)) {
      toast.error('Solde BARKA insuffisant');
      return;
    }

    setConverting(true);
    try {
      const res = await api.post('/wallet/convert', { barka_amount: amount });
      toast.success(`Conversion réussie! +${res.data.tnd_received.toFixed(2)} TND`);
      setConvertDialog(false);
      setConvertAmount('');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de conversion');
    } finally {
      setConverting(false);
    }
  };

  const getTierIcon = (tier) => {
    switch(tier) {
      case 'bronze': return '🥉';
      case 'silver': return '🥈';
      case 'gold': return '🥇';
      case 'diamond': return '💎';
      default: return '🏅';
    }
  };

  const getTierGradient = (tier) => {
    switch(tier) {
      case 'bronze': return 'from-amber-600 to-amber-800';
      case 'silver': return 'from-gray-300 to-gray-500';
      case 'gold': return 'from-yellow-400 to-yellow-600';
      case 'diamond': return 'from-cyan-300 to-blue-500';
      default: return 'from-gray-400 to-gray-600';
    }
  };

  const getTransactionIcon = (type) => {
    if (type.includes('like')) return <Heart className="w-4 h-4 text-red-400" />;
    if (type.includes('comment')) return <MessageCircle className="w-4 h-4 text-blue-400" />;
    if (type.includes('follower')) return <Users className="w-4 h-4 text-purple-400" />;
    if (type.includes('post') || type.includes('recipe')) return <Play className="w-4 h-4 text-green-400" />;
    if (type.includes('challenge')) return <Trophy className="w-4 h-4 text-yellow-400" />;
    if (type.includes('login') || type.includes('streak')) return <Flame className="w-4 h-4 text-orange-400" />;
    if (type.includes('welcome')) return <Gift className="w-4 h-4 text-pink-400" />;
    return <Sparkles className="w-4 h-4 text-cyan-400" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a1628] to-[#1a2744] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  const progressToNextTier = stats?.barka_to_next_tier > 0 
    ? ((wallet?.lifetime_barka || 0) / (wallet?.lifetime_barka + stats.barka_to_next_tier)) * 100
    : 100;

  const canConvert = (wallet?.available_barka || 0) >= (stats?.tier_benefits?.min_withdrawal || 10000);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a1628] to-[#1a2744] text-white pb-24">
      {/* Hero Header - BARKA = Vrai Salaire Message */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 via-red-500/20 to-pink-500/20"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl"></div>
        
        <div className="relative px-4 pt-8 pb-12 max-w-6xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4" />
              Économie Créateur MHARHER
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">
              Tes <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-400">BARKA</span> = Vrai Salaire 💰
            </h1>
            <p className="text-white/70 max-w-xl mx-auto">
              Chaque like, chaque follower, chaque recette te rapproche d'un revenu mensuel réel. 
              Transforme ta passion en profession!
            </p>
          </motion.div>

          {/* Main Balance Cards */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* BARKA Balance */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-white/60 text-sm flex items-center gap-2">
                    <Flame className="w-4 h-4 text-orange-400" />
                    Solde BARKA
                  </p>
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className="text-4xl font-bold text-orange-400" data-testid="barka-balance">
                      {(wallet?.available_barka || 0).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-white/50 text-sm mt-1">
                    ≈ {(stats?.estimated_tnd_value || 0).toFixed(2)} TND
                  </p>
                </div>
                <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${getTierGradient(wallet?.tier)} flex items-center justify-center text-2xl shadow-lg`}>
                  {getTierIcon(wallet?.tier)}
                </div>
              </div>

              {/* Pending */}
              {wallet?.pending_barka > 0 && (
                <div className="flex items-center gap-2 p-2 bg-yellow-500/20 rounded-lg mb-4">
                  <Clock className="w-4 h-4 text-yellow-400" />
                  <span className="text-yellow-200 text-sm">
                    +{wallet.pending_barka.toLocaleString()} en validation
                  </span>
                </div>
              )}

              {/* Convert Button */}
              <Button 
                onClick={() => setConvertDialog(true)}
                disabled={!canConvert}
                className={`w-full ${canConvert 
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600' 
                  : 'bg-white/10 text-white/50'}`}
                data-testid="convert-barka-btn"
              >
                {canConvert ? (
                  <>
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Convertir en TND
                  </>
                ) : (
                  <>
                    <Target className="w-4 h-4 mr-2" />
                    {((stats?.tier_benefits?.min_withdrawal || 10000) - (wallet?.available_barka || 0)).toLocaleString()} BARKA jusqu'à la conversion
                  </>
                )}
              </Button>
            </motion.div>

            {/* TND Balance */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 backdrop-blur-xl rounded-2xl p-6 border border-emerald-500/30"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-white/60 text-sm flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-emerald-400" />
                    Solde TND (Retirable)
                  </p>
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className="text-4xl font-bold text-emerald-400" data-testid="tnd-balance">
                      {(wallet?.tnd_balance || 0).toFixed(2)}
                    </span>
                    <span className="text-xl text-white/60">TND</span>
                  </div>
                  <p className="text-white/50 text-sm mt-1">
                    Minimum retrait: 10 TND
                  </p>
                </div>
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-lg">
                  <Wallet className="w-8 h-8 text-white" />
                </div>
              </div>

              <Button 
                onClick={() => navigate('/settings')}
                disabled={(wallet?.tnd_balance || 0) < 10}
                className={`w-full ${(wallet?.tnd_balance || 0) >= 10 
                  ? 'bg-emerald-500 hover:bg-emerald-600' 
                  : 'bg-white/10 text-white/50'}`}
              >
                <Download className="w-4 h-4 mr-2" />
                {(wallet?.tnd_balance || 0) >= 10 ? 'Retirer mes gains' : 'Convertis des BARKA pour retirer'}
              </Button>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Stats Strip */}
      <div className="max-w-6xl mx-auto px-4 -mt-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8"
        >
          <div className="bg-[#1a2744]/80 backdrop-blur rounded-xl p-4 border border-white/10">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-4 h-4 text-blue-400" />
              <span className="text-white/50 text-xs">Ce mois</span>
            </div>
            <p className="text-xl font-bold text-blue-400">
              +{(stats?.barka_earned_this_month || 0).toLocaleString()}
            </p>
            <p className="text-white/40 text-xs">BARKA</p>
          </div>
          
          <div className="bg-[#1a2744]/80 backdrop-blur rounded-xl p-4 border border-white/10">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-green-400" />
              <span className="text-white/50 text-xs">Revenu estimé</span>
            </div>
            <p className="text-xl font-bold text-green-400">
              {(stats?.estimated_monthly_revenue || 0).toFixed(2)}
            </p>
            <p className="text-white/40 text-xs">TND/mois</p>
          </div>
          
          <div className="bg-[#1a2744]/80 backdrop-blur rounded-xl p-4 border border-white/10">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="w-4 h-4 text-orange-400" />
              <span className="text-white/50 text-xs">Streak</span>
            </div>
            <p className="text-xl font-bold text-orange-400">
              {stats?.current_streak || 0}
            </p>
            <p className="text-white/40 text-xs">jours</p>
          </div>
          
          <div className="bg-[#1a2744]/80 backdrop-blur rounded-xl p-4 border border-white/10">
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 className="w-4 h-4 text-purple-400" />
              <span className="text-white/50 text-xs">Taux conversion</span>
            </div>
            <p className="text-xl font-bold text-purple-400">
              {((stats?.current_conversion_rate || 0.001) * 1000).toFixed(2)}
            </p>
            <p className="text-white/40 text-xs">TND/1000</p>
          </div>
        </motion.div>

        {/* Tier Progress */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="bg-[#1a2744]/80 border-white/10 mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2">
                <Crown className="w-5 h-5 text-yellow-400" />
                Progression Tier Créateur
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{getTierIcon(wallet?.tier)}</span>
                  <span className="text-white font-bold uppercase">{wallet?.tier}</span>
                </div>
                {stats?.next_tier && (
                  <div className="flex items-center gap-2 text-white/50">
                    <span className="text-2xl">{getTierIcon(stats.next_tier)}</span>
                    <span className="uppercase">{stats.next_tier}</span>
                  </div>
                )}
              </div>
              <Progress value={progressToNextTier} className="h-3 mb-3" />
              {stats?.barka_to_next_tier > 0 && (
                <p className="text-white/60 text-sm text-center">
                  <strong className="text-orange-400">{stats.barka_to_next_tier.toLocaleString()}</strong> BARKA pour débloquer le tier <strong className="uppercase">{stats.next_tier}</strong>
                </p>
              )}

              {/* Tier Benefits */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-white/40 text-xs mb-1">Bonus conversion</p>
                  <p className="text-green-400 font-bold text-lg">+{stats?.tier_benefits?.conversion_bonus || 0}%</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-white/40 text-xs mb-1">Frais retrait</p>
                  <p className="text-blue-400 font-bold text-lg">{stats?.tier_benefits?.withdrawal_fee || 5}%</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-white/40 text-xs mb-1">Min. conversion</p>
                  <p className="text-purple-400 font-bold text-lg">{((stats?.tier_benefits?.min_withdrawal || 10000) / 1000).toFixed(0)}K</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-white/40 text-xs mb-1">Commission tips</p>
                  <p className="text-orange-400 font-bold text-lg">{stats?.tier_benefits?.tips_commission || 30}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Earnings Breakdown */}
        {earnings && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card className="bg-[#1a2744]/80 border-white/10 mb-6">
              <CardHeader className="pb-2">
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-cyan-400" />
                  Répartition des gains (ce mois)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-white/70 flex items-center gap-2">
                      <Play className="w-4 h-4 text-green-400" />
                      Création de contenu
                    </span>
                    <span className="text-green-400 font-bold">+{(earnings.content_creation || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-white/70 flex items-center gap-2">
                      <Heart className="w-4 h-4 text-red-400" />
                      Engagement reçu
                    </span>
                    <span className="text-red-400 font-bold">+{(earnings.engagement_received || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-white/70 flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-yellow-400" />
                      Communauté
                    </span>
                    <span className="text-yellow-400 font-bold">+{(earnings.community_participation || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-white/70 flex items-center gap-2">
                      <Zap className="w-4 h-4 text-orange-400" />
                      Streaks & Fidélité
                    </span>
                    <span className="text-orange-400 font-bold">+{(earnings.streaks_and_loyalty || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-white/70 flex items-center gap-2">
                      <Gift className="w-4 h-4 text-pink-400" />
                      Bonus
                    </span>
                    <span className="text-pink-400 font-bold">+{(earnings.bonuses || 0).toLocaleString()}</span>
                  </div>
                  <div className="border-t border-white/10 pt-3 mt-3">
                    <div className="flex items-center justify-between">
                      <span className="text-white font-bold">Total ce mois</span>
                      <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-400">
                        +{(earnings.total || 0).toLocaleString()} BARKA
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Recent Transactions */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Card className="bg-[#1a2744]/80 border-white/10 mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-blue-400" />
                  Activité récente
                </span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate('/wallet')}
                  className="text-white/50 hover:text-white"
                >
                  Voir tout <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {transactions.length === 0 ? (
                <p className="text-white/50 text-center py-6">Aucune activité récente</p>
              ) : (
                <div className="space-y-2">
                  {transactions.slice(0, 8).map((tx, index) => (
                    <motion.div
                      key={tx.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                          {getTransactionIcon(tx.type)}
                        </div>
                        <div>
                          <p className="text-white text-sm font-medium">{tx.description}</p>
                          <p className="text-white/40 text-xs">
                            {new Date(tx.created_at).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                      <span className={`font-bold ${tx.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {tx.amount >= 0 ? '+' : ''}{tx.amount.toLocaleString()}
                      </span>
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* How to Earn More */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <Card className="bg-gradient-to-r from-orange-500/20 to-red-500/20 border-orange-500/30 mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-yellow-400" />
                Maximise tes gains BARKA
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <div className="text-3xl mb-2">📝</div>
                  <p className="text-white font-bold">+50</p>
                  <p className="text-white/60 text-xs">par post</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <div className="text-3xl mb-2">🍳</div>
                  <p className="text-white font-bold">+100</p>
                  <p className="text-white/60 text-xs">par recette</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <div className="text-3xl mb-2">🏆</div>
                  <p className="text-white font-bold">+5000</p>
                  <p className="text-white/60 text-xs">1er au défi</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <div className="text-3xl mb-2">🔥</div>
                  <p className="text-white font-bold">+300</p>
                  <p className="text-white/60 text-xs">streak 30j</p>
                </div>
              </div>
              <div className="mt-4 text-center">
                <Button 
                  onClick={() => navigate('/feed')}
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Commencer à créer
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Convert Dialog */}
      <Dialog open={convertDialog} onOpenChange={setConvertDialog}>
        <DialogContent className="bg-[#1a2744] border-white/20 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-400" />
              Convertir BARKA en TND
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Transforme tes BARKA en dinars tunisiens retirables.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-white/5 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/60">Solde disponible</span>
                <span className="text-orange-400 font-bold">{(wallet?.available_barka || 0).toLocaleString()} BARKA</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/60">Taux actuel</span>
                <span className="text-green-400">{((stats?.current_conversion_rate || 0.001) * 1000).toFixed(2)} TND / 1000 BARKA</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/60">Bonus tier {wallet?.tier}</span>
                <span className="text-purple-400">+{stats?.tier_benefits?.conversion_bonus || 0}%</span>
              </div>
            </div>

            <div>
              <Label className="text-white/80">Montant à convertir</Label>
              <Input
                type="number"
                value={convertAmount}
                onChange={(e) => setConvertAmount(e.target.value)}
                placeholder={`Min. ${(stats?.tier_benefits?.min_withdrawal || 10000).toLocaleString()} BARKA`}
                className="bg-white/10 border-white/20 text-white mt-2"
              />
              {convertAmount && parseFloat(convertAmount) > 0 && (
                <p className="text-green-400 text-sm mt-2">
                  ≈ {(parseFloat(convertAmount) * (stats?.current_conversion_rate || 0.001) * (1 + (stats?.tier_benefits?.conversion_bonus || 0) / 100)).toFixed(2)} TND
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setConvertDialog(false)} className="text-white/60">
              Annuler
            </Button>
            <Button 
              onClick={handleConvert}
              disabled={converting}
              className="bg-gradient-to-r from-orange-500 to-red-500"
            >
              {converting ? 'Conversion...' : 'Confirmer la conversion'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
